import { offlineStorage } from './offlineStorage';
import * as api from './api';

class SyncService {
  constructor() {
    this.isSyncing = false;
    this.syncListeners = [];
    this.isOnline = navigator.onLine;
    this.setupOnlineListener();
  }

  setupOnlineListener() {
    window.addEventListener('online', () => {
      console.log('🌐 Network connection restored');
      this.isOnline = true;
      this.notifyListeners({ type: 'online' });
      this.syncAll();
    });

    window.addEventListener('offline', () => {
      console.log('📴 Network connection lost');
      this.isOnline = false;
      this.notifyListeners({ type: 'offline' });
    });
  }

  addListener(callback) {
    this.syncListeners.push(callback);
  }

  removeListener(callback) {
    this.syncListeners = this.syncListeners.filter(cb => cb !== callback);
  }

  notifyListeners(event) {
    this.syncListeners.forEach(callback => callback(event));
  }

  async syncAll() {
    if (this.isSyncing || !this.isOnline) {
      return;
    }

    this.isSyncing = true;
    this.notifyListeners({ type: 'sync_start' });

    try {
      // Get unsynced entries
      const unsyncedEntries = await offlineStorage.getUnsyncedEntries();
      console.log(`📤 Syncing ${unsyncedEntries.length} unsynced entries...`);

      let successCount = 0;
      let failCount = 0;

      for (const entry of unsyncedEntries) {
        try {
          // Try to create entry on server
          await api.createInwardEntry({
            staff_id: entry.staff_id,
            document_barcode: entry.document_barcode,
            image_urls: entry.image_urls,
            bundles: entry.bundles
          });

          // Mark as synced locally
          await offlineStorage.markAsSynced(entry.entry_id);
          successCount++;

          this.notifyListeners({
            type: 'sync_progress',
            success: successCount,
            failed: failCount,
            total: unsyncedEntries.length
          });
        } catch (error) {
          console.error(`Failed to sync entry ${entry.entry_id}:`, error);
          failCount++;

          // If it's a duplicate error, mark as synced to avoid retry
          if (error.response?.status === 400 && error.response?.data?.detail?.includes('already exists')) {
            await offlineStorage.markAsSynced(entry.entry_id);
            successCount++;
          }
        }
      }

      this.notifyListeners({
        type: 'sync_complete',
        success: successCount,
        failed: failCount,
        total: unsyncedEntries.length
      });

      console.log(`✅ Sync complete: ${successCount} successful, ${failCount} failed`);
    } catch (error) {
      console.error('Sync error:', error);
      this.notifyListeners({ type: 'sync_error', error });
    } finally {
      this.isSyncing = false;
    }
  }

  async getPendingCount() {
    try {
      const unsyncedEntries = await offlineStorage.getUnsyncedEntries();
      return unsyncedEntries.length;
    } catch (error) {
      console.error('Error getting pending count:', error);
      return 0;
    }
  }
}

export const syncService = new SyncService();

// Auto-sync every 30 seconds when online
setInterval(() => {
  if (syncService.isOnline && !syncService.isSyncing) {
    syncService.syncAll();
  }
}, 30000);